import type { AuthedRequester } from "@/lib/brokerAuth";

// BRK-S409 — the account's Financials tab: the insured's financial SUMMARY as
// PMS serves it to a broker, or the fact that there is none yet.
//
// D3 (epic BRK-E023) reverses SUR-S052 answer 1 for THIS SUMMARY ONLY: a
// broker sees the six income-statement and ten balance-sheet lines the October
// wireframe names (BRK-S501), and nothing else from the insured's books — no
// ratio, no score, no tier and no review reason. The two closed lists below
// ARE that decision as data; `test/lib/preapprovalDiscretion.test.ts` pins
// them and keeps every other statement figure out of `src/`.
//
// PMS-S853 (backed by SUR-S270), pinned at BRK-S511 and diffed as a seam
// surface. Every figure is a plain number on the wire (D-10) or NULL — the
// absence of a figure is never zero (SUR-S270) — and a null renders "—". The
// SPA formats for display (with cents) and computes nothing: each column's
// dates and each row's Change percentage are the server's.

const BASE = "/api/broker/accounts";

/** The six income-statement lines, in statement order (PMS-S853, D21) —
 *  `incomeStatement.*.figures` keys. */
export const INCOME_STATEMENT_FIGURES = [
  "revenue",
  "grossProfit",
  "interestExpense",
  "totalExpenses",
  "netOperatingIncome",
  "netIncome",
] as const;
export type IncomeStatementFigure = (typeof INCOME_STATEMENT_FIGURES)[number];

/** The ten balance-sheet lines, in statement order (PMS-S853, D21) —
 *  `balanceSheet.*.figures` keys. */
export const BALANCE_SHEET_FIGURES = [
  "cashAndCashEquivalents",
  "accountsReceivable",
  "prepaidExpenses",
  "inventory",
  "totalCurrentAssets",
  "totalAssets",
  "accountsPayable",
  "totalCurrentLiabilities",
  "totalLiabilities",
  "totalEquity",
] as const;
export type BalanceSheetFigure = (typeof BALANCE_SHEET_FIGURES)[number];

export type FinancialFigure = IncomeStatementFigure | BalanceSheetFigure;

const FIGURE_LABELS: Record<FinancialFigure, string> = {
  revenue: "Revenue",
  grossProfit: "Gross profit",
  interestExpense: "Interest expense",
  totalExpenses: "Total expenses",
  netOperatingIncome: "Net operating income",
  netIncome: "Net income",
  cashAndCashEquivalents: "Cash and cash equivalents",
  accountsReceivable: "Accounts receivable",
  prepaidExpenses: "Pre-paid expenses",
  inventory: "Inventory",
  totalCurrentAssets: "Total current assets",
  totalAssets: "Total assets",
  accountsPayable: "Accounts payable",
  totalCurrentLiabilities: "Total current liabilities",
  totalLiabilities: "Total liabilities",
  totalEquity: "Total equity",
};

/** The wireframe's bold rows: the statements' subtotals. */
const SUBTOTALS: ReadonlySet<FinancialFigure> = new Set<FinancialFigure>([
  "grossProfit",
  "netOperatingIncome",
  "netIncome",
  "totalCurrentAssets",
  "totalAssets",
  "totalCurrentLiabilities",
  "totalLiabilities",
  "totalEquity",
]);

export function financialFigureLabel(key: FinancialFigure): string {
  return FIGURE_LABELS[key];
}

/** A column covering a period (the income statement): ISO dates. A figure
 *  the books do not carry is null — never zero (SUR-S270). */
export interface FinancialPeriodWire<K extends string> {
  from: string;
  to: string;
  figures: Record<K, number | null>;
}

/** A column of balances on one date (the balance sheet): an ISO date. */
export interface FinancialBalancesWire<K extends string> {
  asOf: string;
  figures: Record<K, number | null>;
}

/** The summary as PMS serves it (PMS-S853, backed by SUR-S270). Not
 *  connected carries `connected: false` and nulls. */
export interface AccountFinancialSummaryWire {
  /** Whether the insured's books are linked AND read (SUR-S270). */
  connected: boolean;
  /** ISO `YYYY-MM-DD` of the latest figures. */
  lastUpdated: string | null;
  /** Both columns cover 12 months. */
  incomeStatement: {
    priorYear: FinancialPeriodWire<IncomeStatementFigure>;
    trailingTwelveMonths: FinancialPeriodWire<IncomeStatementFigure>;
    /** The server's change, a percentage (0.6 is +0.6%); null for none. */
    changePercent: Record<IncomeStatementFigure, number | null>;
  } | null;
  /** Balances on each date. */
  balanceSheet: {
    priorYearEnd: FinancialBalancesWire<BalanceSheetFigure>;
    current: FinancialBalancesWire<BalanceSheetFigure>;
    changePercent: Record<BalanceSheetFigure, number | null>;
  } | null;
}

/** One line of a statement as the tab renders it. A figure the server did not
 *  serve as a number is null — shown as "—", never as zero. */
export interface FinancialRow {
  key: FinancialFigure;
  label: string;
  /** A subtotal row, drawn bold. */
  subtotal: boolean;
  prior: number | null;
  current: number | null;
  /** The server's change percentage, or null when it served none. */
  changePercent: number | null;
}

/** A statement: its two columns' served dates (ISO), and its rows. */
export interface FinancialStatement {
  /** The prior column: a period (`from`–`to`) or a date (`to` alone). */
  prior: { from: string | null; to: string };
  current: { from: string | null; to: string };
  rows: FinancialRow[];
}

export type FinancialSummary =
  | { connected: false }
  | {
      connected: true;
      lastUpdated: string | null;
      incomeStatement: FinancialStatement;
      balanceSheet: FinancialStatement;
    };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** A plain finite number, or null. A formatted string is never parsed: one
 *  accepted from the wire is how the next client learns it is allowed. */
const figure = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/;
const isoDate = (v: unknown): string | null =>
  typeof v === "string" && ISO_DATE.test(v) ? v : null;

/** A column's served dates: a period, or a date. Null when unreadable. */
function columnDates(
  column: Record<string, unknown>,
): { from: string | null; to: string } | null {
  const asOf = isoDate(column.asOf);
  if (asOf) return { from: null, to: asOf };
  const from = isoDate(column.from);
  const to = isoDate(column.to);
  return from && to ? { from, to } : null;
}

function readStatement<K extends FinancialFigure>(
  keys: readonly K[],
  statement: unknown,
  priorKey: string,
  currentKey: string,
): FinancialStatement | null {
  if (!isRecord(statement)) return null;
  const prior = statement[priorKey];
  const current = statement[currentKey];
  if (!isRecord(prior) || !isRecord(current)) return null;
  if (!isRecord(prior.figures) || !isRecord(current.figures)) return null;
  const priorDates = columnDates(prior);
  const currentDates = columnDates(current);
  if (!priorDates || !currentDates) return null;
  const priorFigures = prior.figures;
  const currentFigures = current.figures;
  const change = isRecord(statement.changePercent)
    ? statement.changePercent
    : {};
  return {
    prior: priorDates,
    current: currentDates,
    rows: keys.map((key) => ({
      key,
      label: FIGURE_LABELS[key],
      subtotal: SUBTOTALS.has(key),
      prior: figure(priorFigures[key]),
      current: figure(currentFigures[key]),
      changePercent: figure(change[key]),
    })),
  };
}

/**
 * The summary, or null when the body cannot be read — including a connected
 * summary missing a statement or its dates, which is an error and never an
 * empty table.
 */
export function readFinancialSummary(raw: unknown): FinancialSummary | null {
  if (!isRecord(raw) || typeof raw.connected !== "boolean") return null;
  if (!raw.connected) return { connected: false };
  const incomeStatement = readStatement(
    INCOME_STATEMENT_FIGURES,
    raw.incomeStatement,
    "priorYear",
    "trailingTwelveMonths",
  );
  const balanceSheet = readStatement(
    BALANCE_SHEET_FIGURES,
    raw.balanceSheet,
    "priorYearEnd",
    "current",
  );
  if (incomeStatement === null || balanceSheet === null) return null;
  return {
    connected: true,
    lastUpdated: isoDate(raw.lastUpdated),
    incomeStatement,
    balanceSheet,
  };
}

const STATEMENT_MONEY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** A statement figure for display, with cents, as the wireframe shows them
 *  ("$18,007,700.00"). Display only: the wire stays a plain number (D-10). */
export function formatStatementMoney(value: number): string {
  return STATEMENT_MONEY.format(value);
}

/** The server's change percentage, signed, one decimal ("+0.6%"). */
export function formatChangePercent(value: number): string {
  const text = `${Math.abs(value).toFixed(1)}%`;
  if (text === "0.0%") return text;
  return value > 0 ? `+${text}` : `-${text}`;
}

/** GET /api/broker/accounts/{id}/financial-summary (PMS-S853).
 *  Throws on a body it cannot read, so the tab says the read failed. */
export async function getAccountFinancialSummary(
  request: AuthedRequester,
  accountId: string,
): Promise<FinancialSummary> {
  const summary = readFinancialSummary(
    await request<unknown>(
      `${BASE}/${encodeURIComponent(accountId)}/financial-summary`,
    ),
  );
  if (summary === null) throw new Error("Unreadable financial summary.");
  return summary;
}
